import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import toast from "react-hot-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { motion } from "framer-motion";
import { Eye, EyeOff, ChevronDown, Check, Shield, Spade, Diamond, Club, Heart, Coins, Dice5, Crown } from "lucide-react";
import { EXCLUDED_STATE_CODES as RESTRICTED_STATES } from "../config/excludedStates";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a6f5b0e0-0ce2-42cf-b98c-d24c018b20f3/artifacts/cbof8ucc_IMG_9876.jpeg";
const ACCENT = "#4d7cff";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"
];
const STATE_NAMES = {
  AL:"Alabama",AK:"Alaska",AZ:"Arizona",AR:"Arkansas",CA:"California",CO:"Colorado",CT:"Connecticut",
  DE:"Delaware",FL:"Florida",GA:"Georgia",HI:"Hawaii",ID:"Idaho",IL:"Illinois",IN:"Indiana",IA:"Iowa",
  KS:"Kansas",KY:"Kentucky",LA:"Louisiana",ME:"Maine",MD:"Maryland",MA:"Massachusetts",MI:"Michigan",
  MN:"Minnesota",MS:"Mississippi",MO:"Missouri",MT:"Montana",NE:"Nebraska",NV:"Nevada",NH:"New Hampshire",
  NJ:"New Jersey",NM:"New Mexico",NY:"New York",NC:"North Carolina",ND:"North Dakota",OH:"Ohio",
  OK:"Oklahoma",OR:"Oregon",PA:"Pennsylvania",RI:"Rhode Island",SC:"South Carolina",SD:"South Dakota",
  TN:"Tennessee",TX:"Texas",UT:"Utah",VT:"Vermont",VA:"Virginia",WA:"Washington",WV:"West Virginia",
  WI:"Wisconsin",WY:"Wyoming",DC:"Washington D.C."
};

const SignUp = () => {
  const navigate = useNavigate();
  const { user, register: authRegister } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [territoryChecked, setTerritoryChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [refCodeStatus, setRefCodeStatus] = useState(null); // null | { valid, referrer_username } | { valid: false }
  const [validatingCode, setValidatingCode] = useState(false);

  useEffect(() => { if (user) navigate("/lobby"); }, [user, navigate]);

  // Debounced live referral code validation (endpoint searches both users + waitlist codes)
  useEffect(() => {
    const code = (referralCode || "").trim().toUpperCase();
    if (!code || code.length < 4) { setRefCodeStatus(null); return; }
    setValidatingCode(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/auth/waitlist/validate-code/${encodeURIComponent(code)}`);
        setRefCodeStatus(res.data);
      } catch (_) { setRefCodeStatus({ valid: false }); }
      setValidatingCode(false);
    }, 400);
    return () => clearTimeout(t);
  }, [referralCode]);

  // Capture ?ref=CODE from the URL (and persist it in case user refreshes)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = (params.get("ref") || localStorage.getItem("jackroyals_ref") || "").trim().toUpperCase();
    if (ref) {
      setReferralCode(ref);
      localStorage.setItem("jackroyals_ref", ref);
    }
  }, []);

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!confirmChecked) { toast.error("You must confirm you are 18+ and agree to the Terms"); return; }
    if (!territoryChecked) { toast.error("You must acknowledge the territory eligibility rules"); return; }
    if (!firstName.trim() || !lastName.trim()) { toast.error("First and last name are required"); return; }
    if (!username.trim()) { toast.error("Username is required"); return; }
    if (!state) { toast.error("Please select your state"); return; }
    if (password.length < 8) { toast.error("Password must be at least 8 characters with a letter and number"); return; }
    if (password !== confirmPassword) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      // Pull QR attribution from URL param OR sessionStorage (set by /win page)
      const urlParams = new URLSearchParams(window.location.search);
      const qrSource = (urlParams.get("qr") || sessionStorage.getItem("jr_qr_source") || "").trim().toLowerCase() || undefined;
      await authRegister({
        email,
        password,
        username: username.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        state,
        zip_code: zipCode,
        phone: phone ? `+1${phone.replace(/\D/g, "")}` : undefined,
        referral_code: referralCode || undefined,
        qr_source: qrSource,
        // Legal acknowledgements — server-side enforced + audit-logged
        agree_to_terms:     confirmChecked,
        agree_to_territory: territoryChecked,
        terms_version:      "2026-06-22",
      });
      // Clear stored attribution once successfully used
      if (referralCode) localStorage.removeItem("jackroyals_ref");
      sessionStorage.removeItem("jr_qr_source");
      toast.success("Welcome to JackRoyals! 300K GC + 2 SC welcome bonus added 🎉");
      navigate("/lobby");
    } catch (error) {
      const msg = error.response?.data?.detail || error.response?.data?.message || error.message || "Failed to sign up";
      toast.error(msg);
    }
    finally { setLoading(false); }
  };

  const inputStyle = { background: '#0a0f1f', borderColor: '#1f2a4d' };
  const labelClass = "text-[#8fa2d6] text-xs font-semibold mb-1.5 block";

  return (
    <div className="min-h-screen text-white flex flex-col items-center px-4 relative overflow-hidden" style={{ background: 'radial-gradient(circle at top, #151b3a 0%, #050814 55%, #02030a 100%)' }}>
      <div className="fixed inset-0 z-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(77,124,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(77,124,255,0.3) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

      {/* Floating casino characters — purely decorative */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
        {[
          { Icon: Spade,   x: "6%",  y: "12%", size: 38, color: "#4d7cff", delay: 0,   rotate: -12, drift: 14 },
          { Icon: Heart,   x: "88%", y: "18%", size: 34, color: "#ff5577", delay: 0.6, rotate:  18, drift: -12 },
          { Icon: Diamond, x: "12%", y: "78%", size: 32, color: "#7f5dff", delay: 1.0, rotate:  -8, drift:  18 },
          { Icon: Club,    x: "84%", y: "70%", size: 36, color: "#34d399", delay: 0.3, rotate:  14, drift: -16 },
          { Icon: Coins,   x: "3%",  y: "42%", size: 40, color: "#ffd700", delay: 0.8, rotate:  -6, drift:  12 },
          { Icon: Dice5,   x: "92%", y: "44%", size: 38, color: "#ffd700", delay: 0.4, rotate:  10, drift: -14 },
          { Icon: Crown,   x: "50%", y: "6%",  size: 30, color: "#ffd700", delay: 0.2, rotate:   0, drift:  10 },
          { Icon: Spade,   x: "48%", y: "92%", size: 26, color: "#7f5dff", delay: 1.2, rotate:  20, drift:  -8 },
        ].map((c, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{ left: c.x, top: c.y, color: c.color, filter: `drop-shadow(0 0 14px ${c.color}55)` }}
            initial={{ opacity: 0, scale: 0.6, rotate: c.rotate }}
            animate={{
              opacity: [0, 0.55, 0.45],
              y: [0, c.drift, 0],
              rotate: [c.rotate, c.rotate + 6, c.rotate],
            }}
            transition={{
              opacity: { duration: 1.4, delay: c.delay },
              y: { duration: 6 + (i % 3), repeat: Infinity, ease: "easeInOut", delay: c.delay },
              rotate: { duration: 7 + (i % 4), repeat: Infinity, ease: "easeInOut", delay: c.delay },
            }}
          >
            <c.Icon size={c.size} strokeWidth={1.6} fill={["Coins","Crown","Dice5"].includes(c.Icon.displayName || c.Icon.name) ? "none" : c.color} fillOpacity={0.18} />
          </motion.div>
        ))}
      </div>

      {/* Top nav */}
      <div className="fixed top-0 w-full z-50 backdrop-blur-xl border-b" style={{ background: 'rgba(5,8,20,0.92)', borderColor: 'rgba(120,150,255,0.25)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="signup-logo-link">
            <img src={LOGO_URL} alt="JackRoyals" className="w-10 h-10 rounded-xl object-cover" />
          </Link>
          <Link to="/" className="text-[#8fa2d6] hover:text-white transition-colors font-medium text-sm" data-testid="signup-back-link">Back to Home</Link>
        </div>
      </div>

      <div className="relative z-10 w-full max-w-md pt-20 pb-12">
        {/* Header */}
        <motion.div className="flex flex-col items-center mb-6" initial={{ opacity: 0, y: -15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <img src={LOGO_URL} alt="JackRoyals" className="w-16 h-16 rounded-2xl object-cover mb-3 shadow-lg" style={{ boxShadow: `0 0 30px ${ACCENT}30` }} />
          <h1 className="text-2xl sm:text-3xl font-black" data-testid="signup-heading">
            Sign Up &amp; Play
          </h1>
          <p className="text-[#8fa2d6] text-sm mt-1">
            300,000 GC + 2 SC welcome bonus — instant access, no waiting.
          </p>
        </motion.div>

        <>
        {/* Welcome bonus banner */}
        <motion.div
          className="rounded-xl border p-4 mb-4 text-center"
          style={{ background: 'linear-gradient(135deg, rgba(52,211,153,0.10), rgba(77,124,255,0.05))', borderColor: 'rgba(52,211,153,0.3)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          data-testid="signup-welcome-bonus-banner"
        >
          <p className="text-emerald-300 font-black text-sm">🎁 INSTANT WELCOME BONUS</p>
          <p className="text-emerald-200/70 text-[11px] mt-1">300,000 Gold Coins + 2 Sweeps Coins credited the second you sign up. Extra Sweeps Coins available with purchase or via free AMOE.</p>
        </motion.div>

        <motion.div className="rounded-2xl border p-5 sm:p-7" style={{ background: 'rgba(12,18,45,0.96)', borderColor: 'rgba(120,150,255,0.25)', boxShadow: `0 0 20px ${ACCENT}10` }} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }} data-testid="signup-card">
          <p className="text-[#8fa2d6] text-xs text-center mb-5 tracking-wide">18+ U.S. Sweepstakes &bull; No Purchase Necessary</p>

          <form onSubmit={handleSignUp} className="space-y-3">
            {referralCode && refCodeStatus?.valid ? (
              <div className="p-3 rounded-lg border flex items-center justify-between gap-2" style={{ background: 'rgba(34,197,94,0.10)', borderColor: 'rgba(34,197,94,0.40)' }} data-testid="signup-referral-badge">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-lg">✅</span>
                  <div className="min-w-0">
                    <p className="text-xs text-emerald-300">Code valid — referred by <span className="font-bold text-emerald-200">@{refCodeStatus.referrer_username}</span></p>
                    <p className="text-sm font-bold text-amber-400 font-mono tracking-wider truncate">{referralCode}</p>
                  </div>
                </div>
                <button type="button" onClick={() => { setReferralCode(""); setRefCodeStatus(null); localStorage.removeItem("jackroyals_ref"); }} className="text-[10px] text-[#8fa2d6] hover:text-white underline" data-testid="signup-referral-clear">
                  Remove
                </button>
              </div>
            ) : (
              <div>
                <label className={labelClass}>Friend's Referral Code <span className="text-[#5a6b8a] font-normal">(optional)</span></label>
                <div className="relative">
                  <Input
                    placeholder="Got a code from a friend? Enter it here"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase().trim())}
                    maxLength={12}
                    className={`h-11 rounded-lg text-white text-sm placeholder:text-[#5a6b8a] font-mono tracking-wider uppercase pr-10 ${referralCode && refCodeStatus && !refCodeStatus.valid ? "border-red-500/60" : ""}`}
                    style={inputStyle}
                    data-testid="signup-referral-input"
                  />
                  {referralCode && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold pointer-events-none">
                      {validatingCode ? (
                        <span className="text-[#8fa2d6] animate-pulse">…</span>
                      ) : refCodeStatus?.valid ? (
                        <span className="text-emerald-400">✓</span>
                      ) : refCodeStatus && !refCodeStatus.valid ? (
                        <span className="text-red-400">✗</span>
                      ) : null}
                    </div>
                  )}
                </div>
                {referralCode && refCodeStatus && !refCodeStatus.valid && !validatingCode && (
                  <p className="text-[10px] text-red-400 mt-1" data-testid="signup-referral-invalid">Code not recognized. Double-check with your friend.</p>
                )}
                {(!referralCode || !refCodeStatus) && (
                  <p className="text-[10px] text-[#8fa2d6] mt-1">Friends earn +2 SC + 10 entries when you join with their code.</p>
                )}
              </div>
            )}

            {/* Name row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>First Name</label>
                <Input placeholder="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required className="h-11 rounded-lg text-white text-sm placeholder:text-[#5a6b8a]" style={inputStyle} data-testid="signup-first-name" />
              </div>
              <div>
                <label className={labelClass}>Last Name</label>
                <Input placeholder="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} required className="h-11 rounded-lg text-white text-sm placeholder:text-[#5a6b8a]" style={inputStyle} data-testid="signup-last-name" />
              </div>
            </div>

            {/* Username */}
            <div>
              <label className={labelClass}>Username</label>
              <Input placeholder="Choose a username" value={username} onChange={(e) => setUsername(e.target.value)} required className="h-11 rounded-lg text-white text-sm placeholder:text-[#5a6b8a]" style={inputStyle} data-testid="signup-username" />
            </div>

            {/* State & Zip */}
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <label className={labelClass}>State</label>
                <button
                  type="button"
                  onClick={() => setShowStateDropdown(!showStateDropdown)}
                  className="h-11 w-full rounded-lg text-sm flex items-center justify-between px-3 border"
                  style={inputStyle}
                  data-testid="signup-state-btn"
                >
                  <span className={state ? "text-white" : "text-[#5a6b8a]"}>
                    {state ? `${state} - ${STATE_NAMES[state]?.substring(0, 12)}` : "Select State"}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-[#5a6b8a] transition-transform ${showStateDropdown ? "rotate-180" : ""}`} />
                </button>
                {showStateDropdown && (
                  <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border" style={{ background: '#0a0f1f', borderColor: '#1f2a4d' }}>
                    {US_STATES.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => { setState(s); setShowStateDropdown(false); }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-[#4d7cff]/10 transition-colors flex items-center justify-between ${
                          RESTRICTED_STATES.includes(s) ? "text-red-400/80" : "text-zinc-300"
                        } ${state === s ? "bg-[#4d7cff]/15 text-[#4d7cff]" : ""}`}
                        data-testid={`state-option-${s}`}
                      >
                        <span>{s} - {STATE_NAMES[s]}</span>
                        {RESTRICTED_STATES.includes(s) && <span className="text-[10px] text-red-400/60">Restricted</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className={labelClass}>Zip Code</label>
                <Input placeholder="Zip Code" value={zipCode} onChange={(e) => setZipCode(e.target.value)} className="h-11 rounded-lg text-white text-sm placeholder:text-[#5a6b8a]" style={inputStyle} data-testid="signup-zip" />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className={labelClass}>Email</label>
              <Input type="email" placeholder="Your email address" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11 rounded-lg text-white text-sm placeholder:text-[#5a6b8a]" style={inputStyle} data-testid="signup-email-input" />
            </div>

            {/* Phone */}
            <div>
              <label className={labelClass}>Phone <span className="text-[#5a6b8a] font-normal">(optional)</span></label>
              <div className="flex items-center h-11 rounded-lg border overflow-hidden" style={inputStyle}>
                <span className="px-3 text-sm text-[#8fa2d6] border-r" style={{ borderColor: '#1f2a4d' }}>+1</span>
                <input
                  type="tel"
                  placeholder="Phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/[^\d\-() ]/g, "").substring(0, 14))}
                  className="flex-1 h-full px-3 text-sm text-white placeholder:text-[#5a6b8a] bg-transparent outline-none"
                  data-testid="signup-phone"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className={labelClass}>Password</label>
              <div className="relative">
                <Input type={showPassword ? "text" : "password"} placeholder="Min 8 chars, 1 letter, 1 number" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11 rounded-lg pr-10 text-white text-sm placeholder:text-[#5a6b8a]" style={inputStyle} data-testid="signup-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5a6b8a] hover:text-[#c7d3ff]" tabIndex={-1}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className={labelClass}>Confirm Password</label>
              <Input type={showPassword ? "text" : "password"} placeholder="Confirm your password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="h-11 rounded-lg text-white text-sm placeholder:text-[#5a6b8a]" style={inputStyle} data-testid="signup-confirm-password-input" />
            </div>

            {/* Compliance Section */}
            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'rgba(120,150,255,0.15)' }}>
              {/* JR Branding header for compliance */}
              <div className="flex items-center gap-2.5 mb-4">
                <img src={LOGO_URL} alt="JackRoyals" className="w-8 h-8 rounded-lg object-cover" />
                <div>
                  <p className="text-sm font-bold"><span className="text-white">Jack</span><span style={{ color: ACCENT }}>Royals</span></p>
                  <p className="text-[10px] text-[#5a6b8a]">Arcade Sweepstakes</p>
                </div>
              </div>

              {/* Checkbox 1: Confirmation */}
              <label className="flex items-start gap-3 cursor-pointer mb-3" data-testid="signup-confirm-checkbox-label">
                <div className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${confirmChecked ? "border-[#4d7cff] bg-[#4d7cff]" : "border-[#3a4566]"}`}>
                  {confirmChecked && <Check className="w-3.5 h-3.5 text-white" />}
                </div>
                <input type="checkbox" checked={confirmChecked} onChange={(e) => setConfirmChecked(e.target.checked)} className="sr-only" data-testid="signup-confirm-checkbox" />
                <span className="text-[#8fa2d6] text-xs leading-relaxed">
                  <strong className="text-white">I confirm the following:</strong> I am at least 18 years old; I agree to the{" "}
                  <Link to="/terms" className="underline" style={{ color: ACCENT }}>Terms and Conditions</Link> (which includes a provision requiring arbitration of disputes) and{" "}
                  <Link to="/privacy" className="underline" style={{ color: ACCENT }}>Privacy Policy</Link>.
                  I do not have any existing JackRoyals accounts. I understand JackRoyals has the right to cancel all duplicate accounts and withhold winnings made with such accounts.
                </span>
              </label>

              {/* Checkbox 2: Territory Rules */}
              <label className="flex items-start gap-3 cursor-pointer" data-testid="signup-territory-checkbox-label">
                <div className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${territoryChecked ? "border-[#4d7cff] bg-[#4d7cff]" : "border-[#3a4566]"}`}>
                  {territoryChecked && <Check className="w-3.5 h-3.5 text-white" />}
                </div>
                <input type="checkbox" checked={territoryChecked} onChange={(e) => setTerritoryChecked(e.target.checked)} className="sr-only" data-testid="signup-territory-checkbox" />
                <span className="text-[#8fa2d6] text-xs leading-relaxed">
                  <strong className="text-white">I understand that:</strong><br />
                  a. if I live in an <strong className="text-white">Excluded Territory</strong>, as defined in the Terms & Conditions (Washington, Idaho, Nevada, Montana, and Michigan), I am not eligible to play JackRoyals' games in either Standard or Promotional Play mode; and<br />
                  b. if I am not an <strong className="text-white">Eligible Participant</strong>, as defined in the{" "}
                  <Link to="/sweepstakes-rules" className="underline" style={{ color: ACCENT }}>Sweeps Rules</Link>, I am not eligible to play JackRoyals' games in Promotional Play mode.
                </span>
              </label>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading || !confirmChecked || !territoryChecked}
              className="w-full h-12 text-white font-black text-sm uppercase tracking-wider rounded-full mt-4 disabled:opacity-40"
              style={{ background: confirmChecked && territoryChecked ? `linear-gradient(135deg, ${ACCENT}, #7f5dff)` : '#2a3452', boxShadow: confirmChecked && territoryChecked ? `0 0 16px ${ACCENT}70` : 'none' }}
              data-testid="signup-submit-btn"
            >
              {loading ? "Creating Account..." : "Sign Up & Play Now"}
            </Button>
          </form>

          {/* Welcome bonus */}
          <div className="text-center mt-5 pt-4 border-t" style={{ borderColor: 'rgba(120,150,255,0.15)' }}>
            <p className="font-black text-lg" style={{ color: ACCENT }} data-testid="signup-bonus-text">300,000 GC + 2 SC FREE</p>
            <p className="text-[#5a6b8a] text-xs">Welcome Bonus — No Purchase Required</p>
          </div>
        </motion.div>

        <motion.p className="text-center text-[#5a6b8a] text-sm mt-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          Already have an account?{" "}
          <Link to="/login" className="font-semibold hover:underline" style={{ color: ACCENT }} data-testid="signup-login-link">Log In</Link>
        </motion.p>

        <motion.div className="flex items-center justify-center gap-2 mt-3 text-[#5a6b8a] text-xs" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
          <Shield className="w-3 h-3" />
          <span>U.S. Sweepstakes Compliant &bull; Secure &bull; Fair Play</span>
        </motion.div>
        </>
      </div>
    </div>
  );
};

export default SignUp;
